import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from '../context/AppContext';
import { StopRecord } from '../types';
import Card from '../components/common/Card';
import ConsumptionChart from '../components/history/ConsumptionChart';
import { FunnelIcon, ArrowPathIcon, EyeIcon, ChartBarIcon, TableCellsIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import RecordDetailModal from '../components/history/RecordDetailModal';
import CopyIdBadge from '../components/common/CopyIdBadge';

const History: React.FC = () => {
  const { stopRecords, meters, currentUser, updateStopRecord } = useContext(AppContext);
  const [filterReason, setFilterReason] = useState('');
  const [filterMeterId, setFilterMeterId] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const [selectedRecordToView, setSelectedRecordToView] = useState<StopRecord | null>(null);

  // Tabs & Pagination
  const [activeTab, setActiveTab] = useState<'table' | 'dashboard'>('table');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const completedRecords = useMemo(() =>
    stopRecords
      .filter(r => r.endTime && r.endValue !== undefined)
      .sort((a, b) => b.startTime - a.startTime),
    [stopRecords]);

  const uniqueAreas = useMemo(() => [...new Set(meters.map(m => m.area))], [meters]);

  const filteredMetersForDropdown = useMemo(() => {
    if (!filterArea) return meters;
    return meters.filter(m => m.area === filterArea);
  }, [meters, filterArea]);

  const handleAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setFilterArea(newArea);
    if (newArea && filterMeterId) {
      const meterStillValid = meters.some(m => m.id === filterMeterId && m.area === newArea);
      if (!meterStillValid) {
        setFilterMeterId('');
      }
    }
  };

  const filteredRecords = useMemo(() => {
    return completedRecords.filter(record => {
      const meter = meters.find(m => m.id === record.meterId);
      if (!meter) return false;

      const reasonMatch = filterReason ? record.reason.toLowerCase().includes(filterReason.toLowerCase()) : true;
      const meterMatch = filterMeterId ? record.meterId === filterMeterId : true;
      const areaMatch = filterArea ? meter.area === filterArea : true;

      let dateMatch = true;
      if (startDate || endDate) {
        const recordDate = new Date(record.startTime);
        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (recordDate < start) dateMatch = false;
        }
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (recordDate > end) dateMatch = false;
        }
      }
      return reasonMatch && meterMatch && areaMatch && dateMatch;
    });
  }, [completedRecords, filterReason, filterMeterId, filterArea, startDate, endDate, meters]);

  const selectedRecords = useMemo(() => {
    return completedRecords.filter(record => selectedRecordIds.includes(record.id));
  }, [completedRecords, selectedRecordIds]);

  const handleSelectRecord = (recordId: string) => {
    setSelectedRecordIds(prev =>
      prev.includes(recordId)
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  const clearFilters = () => {
    setFilterMeterId('');
    setFilterReason('');
    setFilterArea('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  // Pagination Logic
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(start, start + itemsPerPage);
  }, [filteredRecords, currentPage]);

  const isAllSelected = paginatedRecords.length > 0 && paginatedRecords.every(r => selectedRecordIds.includes(r.id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRecordIds(prev => prev.filter(id => !paginatedRecords.some(r => r.id === id)));
    } else {
      const newIds = paginatedRecords.map(r => r.id).filter(id => !selectedRecordIds.includes(id));
      setSelectedRecordIds(prev => [...prev, ...newIds]);
    }
  };

  // Reset to first page when filtering
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterReason, filterMeterId, filterArea, startDate, endDate]);

  const formatDuration = (start: number, end: number) => {
    const diff = (end - start) / 1000;
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = Math.floor(diff % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)] md:h-[calc(100vh-4rem)] lg:h-[calc(100vh-5rem)] animate-in fade-in duration-500 w-full pb-4">

      {/* TABS (Now at the top) */}
      <div className="shrink-0 bg-white border-b border-gray-200 pt-3 pb-0 mb-4 flex space-x-2 shadow-sm rounded-xl px-4 sm:px-2 transition-all">
        <button
          onClick={() => setActiveTab('table')}
          className={`py-3 px-6 inline-flex items-center gap-2 font-medium text-sm transition-colors relative ${activeTab === 'table' ? 'text-brand-primary' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
        >
          <TableCellsIcon className="h-5 w-5" />
          Tabla de Datos
          {activeTab === 'table' && <span className="absolute bottom-0 left-0 w-full h-1 bg-brand-primary rounded-t-md"></span>}
        </button>
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`py-3 px-6 inline-flex items-center gap-2 font-medium text-sm transition-colors relative ${activeTab === 'dashboard' ? 'text-brand-primary' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
        >
          <ChartBarIcon className="h-5 w-5" />
          Dashboard y Análisis IA
          {activeTab === 'dashboard' && <span className="absolute bottom-0 left-0 w-full h-1 bg-brand-primary rounded-t-md"></span>}
          {selectedRecords.length > 0 && (
            <span className="ml-2 bg-brand-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              {selectedRecords.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'dashboard' && (
        <div className="flex-1 overflow-y-auto animate-in fade-in duration-300 pb-8 pr-2">
          <ConsumptionChart selectedRecords={selectedRecords} meters={meters} />
        </div>
      )}

      {activeTab === 'table' && (
        <div className="flex-1 flex flex-col min-h-0 space-y-4">
          <Card className="shrink-0 shadow-sm border border-gray-100 mb-0">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <FunnelIcon className="h-5 w-5 mr-2 text-gray-500" />
              Filtros de Búsqueda
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
              <div>
                <label htmlFor="areaFilter" className="block text-sm font-semibold text-gray-700 mb-1.5">Área o Sector</label>
                <select id="areaFilter" value={filterArea} onChange={handleAreaChange} className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-2.5 bg-gray-50 hover:bg-white transition-colors border">
                  <option value="">Todas las áreas</option>
                  {uniqueAreas.map(area => <option key={area} value={area}>{area}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="meterFilter" className="block text-sm font-semibold text-gray-700 mb-1.5">Equipo / Medidor</label>
                <select id="meterFilter" value={filterMeterId} onChange={e => setFilterMeterId(e.target.value)} className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-2.5 bg-gray-50 hover:bg-white transition-colors border" disabled={filteredMetersForDropdown.length === 0}>
                  <option value="">Todos los equipos</option>
                  {filteredMetersForDropdown.map(meter => <option key={meter.id} value={meter.id}>{meter.name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="reasonFilter" className="block text-sm font-semibold text-gray-700 mb-1.5">Motivo de Parada</label>
                <input type="text" id="reasonFilter" placeholder="Ej: CIP, Mantenimiento..." value={filterReason} onChange={e => setFilterReason(e.target.value)} className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-2.5 bg-gray-50 hover:bg-white transition-colors border placeholder-gray-400" />
              </div>
              <div>
                <label htmlFor="startDate" className="block text-sm font-semibold text-gray-700 mb-1.5">Fecha Inicio</label>
                <input type="date" id="startDate" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-2.5 bg-gray-50 hover:bg-white transition-colors border text-gray-700 uppercase" />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-semibold text-gray-700 mb-1.5">Fecha Fin</label>
                <input type="date" id="endDate" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-2.5 bg-gray-50 hover:bg-white transition-colors border text-gray-700 uppercase" />
              </div>
              <div className="flex items-end">
                <button onClick={clearFilters} className="w-full flex items-center justify-center px-4 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-all shadow-sm active:scale-95">
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Limpiar Filtros
                </button>
              </div>
            </div>
          </Card>

          <Card className="flex-1 min-h-0 flex flex-col overflow-hidden p-0 shadow-sm border border-gray-100">
            <div className="shrink-0 p-4 border-b border-gray-100 bg-white flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Registros Históricos de Carga Base</h2>
              <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{filteredRecords.length} registros</span>
            </div>
            <div className="flex-1 overflow-auto bg-white relative">

              {/* MOBILE CARD VIEW */}
              <div className="md:hidden divide-y divide-gray-100">
                {paginatedRecords.map(record => {
                  const meter = meters.find(m => m.id === record.meterId);
                  const consumption = record.endValue! - record.startValue;
                  const durationHours = (record.endTime! - record.startTime) / (1000 * 60 * 60);
                  const consumptionPerHour = durationHours > 0 ? consumption / durationHours : 0;
                  const isSelected = selectedRecordIds.includes(record.id);

                  return (
                    <div key={record.id} className={`p-4 space-y-3 transition-colors ${isSelected ? 'bg-amber-50' : 'bg-white hover:bg-gray-50'}`}>
                      <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            className="h-5 w-6 text-brand-primary border-gray-300 rounded focus:ring-brand-primary cursor-pointer"
                            checked={isSelected}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                          <div>
                            <div className="text-sm font-bold text-gray-900">{new Date(record.startTime).toLocaleDateString()}</div>
                            <div className="text-xs text-gray-500">{new Date(record.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedRecordToView(record)}
                          className="p-1.5 text-gray-400 hover:text-brand-primary hover:bg-amber-50 rounded-lg transition-colors"
                          title="Ver Detalles"
                        >
                          <EyeIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Equipo</p>
                          <div className="text-sm font-bold text-gray-800">{meter?.name || 'N/A'}</div>
                          <div className="text-xs text-gray-500 truncate">{meter?.area || 'N/A'}</div>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Motivo</p>
                          <div className="text-sm text-gray-800 font-medium truncate">{record.reason}</div>
                          <div className="text-xs text-gray-500 truncate">{record.supervisorName || 'N/A'}</div>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Consumo</p>
                          <div className="text-sm font-black text-brand-primary">
                            {consumption.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className="text-xs font-bold ml-1">{meter?.unit}</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Eficiencia</p>
                          <div className="text-sm font-black text-[#F46800]">
                            {consumptionPerHour.toFixed(2)} <span className="text-xs font-bold">{meter?.unit}/h</span>
                          </div>
                          <div className="text-[10px] font-bold text-gray-500 mt-0.5">⏱ {durationHours.toFixed(2)} h</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* DESKTOP TABLE VIEW */}
              <table className="hidden md:table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-16">
                      <input
                        type="checkbox"
                        className="h-5 w-5 text-brand-primary border-gray-300 rounded focus:ring-brand-primary cursor-pointer transition-transform hover:scale-110"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        title="Seleccionar todos en esta página"
                      />
                    </th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha / Hora</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Equipo / Área</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Motivo / Supervisor</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Consumo Total</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Tiempo (Hrs)</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-center text-xs font-bold text-[#F46800] uppercase tracking-wider bg-orange-50/50">Eficiencia (Consumo/Hora)</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedRecords.map(record => {
                    const meter = meters.find(m => m.id === record.meterId);
                    const consumption = record.endValue! - record.startValue;
                    const durationHours = (record.endTime! - record.startTime) / (1000 * 60 * 60);
                    const consumptionPerHour = durationHours > 0 ? consumption / durationHours : 0;
                    const isSelected = selectedRecordIds.includes(record.id);

                    return (
                      <tr key={record.id} className={`hover:bg-amber-50/50 transition-colors cursor-default ${isSelected ? 'bg-amber-50' : ''}`}>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-5 w-5 text-brand-primary border-gray-300 rounded focus:ring-brand-primary cursor-pointer transition-transform hover:scale-110"
                            checked={isSelected}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{new Date(record.startTime).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">{new Date(record.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {formatDuration(record.startTime, record.endTime!)} h</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-800">{meter?.name || 'N/A'}</div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide mt-0.5">{meter?.area || 'N/A'}</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-800 font-medium">{record.reason}</div>
                          <div className="text-xs text-gray-500 flex items-center mt-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-1.5"></span>
                            {record.supervisorName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="text-sm font-black text-brand-primary">
                            {consumption.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className="text-xs text-brand-primary/70 font-bold ml-1">{meter?.unit}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-bold text-gray-700">
                            {durationHours.toFixed(2)} <span className="text-xs font-medium text-gray-500">h</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-center bg-orange-50/10">
                          <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-white border border-orange-100 shadow-sm">
                            <span className="text-sm font-black text-[#F46800]">{consumptionPerHour.toFixed(2)}</span>
                            <span className="text-xs font-bold text-[#F46800]/70 ml-1">{meter?.unit}/h</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => setSelectedRecordToView(record)}
                            className="p-1.5 text-gray-400 hover:text-brand-primary hover:bg-amber-50 rounded-lg transition-colors inline-block focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            title="Ver Detalles Completos y Fotos"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredRecords.length === 0 && (
                <div className="text-center p-12">
                  <p className="text-gray-500 font-medium text-lg mb-1">No se encontraron registros</p>
                  <p className="text-gray-400 text-sm">Prueba ajustando los filtros de búsqueda superior.</p>
                </div>
              )}
            </div>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="shrink-0 bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * itemsPerPage, filteredRecords.length)}
                      </span>{' '}
                      de <span className="font-medium">{filteredRecords.length}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                      >
                        <span className="sr-only">Anterior</span>
                        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => setCurrentPage(i + 1)}
                          className={`relative inline-flex items-center px-4 py-2 border ${currentPage === i + 1 ? 'z-10 bg-brand-primary border-brand-primary text-white' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                      >
                        <span className="sr-only">Siguiente</span>
                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      <RecordDetailModal
        isOpen={!!selectedRecordToView}
        onClose={() => setSelectedRecordToView(null)}
        record={selectedRecordToView}
        meter={meters.find(m => m.id === selectedRecordToView?.meterId) || null}
        isAdmin={currentUser?.role === 'Administrador'}
        onUpdate={updateStopRecord}
      />
    </div>
  );
};

export default History;
